import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileText, Heart, Shield, Users, AlertCircle } from 'lucide-react';

export const TravelerDetailsForm = ({ tripType, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    // Passport Info
    passportNumber: '',
    passportExpiry: '',
    passportCountry: '',
    
    // Emergency Contact
    emergencyName: '',
    emergencyPhone: '',
    emergencyRelationship: '',
    
    // Health & Dietary
    dietaryRestrictions: '',
    allergies: '',
    medicalConditions: '',
    
    // Travel Insurance
    hasInsurance: '',
    insuranceProvider: '',
    insurancePolicyNumber: '',
    
    // Roommate Request (for group tours)
    roommateRequest: ''
  });

  const [errors, setErrors] = useState({});

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.passportNumber.trim()) newErrors.passportNumber = 'Passport number is required';
    if (!formData.passportExpiry) newErrors.passportExpiry = 'Passport expiry is required';
    if (!formData.emergencyName.trim()) newErrors.emergencyName = 'Emergency contact name is required';
    if (!formData.emergencyPhone.trim()) newErrors.emergencyPhone = 'Emergency contact phone is required';
    if (!formData.emergencyRelationship.trim()) newErrors.emergencyRelationship = 'Relationship is required';
    if (!formData.hasInsurance) newErrors.hasInsurance = 'Please indicate if you have travel insurance';
    
    // Passport expiry must be in the future
    if (formData.passportExpiry) {
      const expiryDate = new Date(formData.passportExpiry);
      const sixMonthsFromNow = new Date();
      sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
      if (expiryDate < sixMonthsFromNow) {
        newErrors.passportExpiry = 'Passport must be valid for at least 6 months';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const isGroupTour = tripType === 'group' || tripType === 'both';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Passport Information */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5 text-secondary" />
            Passport Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-medium mb-1 block">Passport Number *</Label>
              <Input
                placeholder="Enter passport number"
                value={formData.passportNumber}
                onChange={(e) => updateField('passportNumber', e.target.value)}
                className={errors.passportNumber ? 'border-red-500' : ''}
              />
              {errors.passportNumber && <p className="text-red-500 text-xs mt-1">{errors.passportNumber}</p>}
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-1 block">Expiration Date *</Label>
              <Input
                type="date"
                value={formData.passportExpiry}
                onChange={(e) => updateField('passportExpiry', e.target.value)}
                className={errors.passportExpiry ? 'border-red-500' : ''}
              />
              {errors.passportExpiry && <p className="text-red-500 text-xs mt-1">{errors.passportExpiry}</p>}
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-1 block">Issuing Country</Label>
              <Input
                placeholder="e.g., United States"
                value={formData.passportCountry}
                onChange={(e) => updateField('passportCountry', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Emergency Contact */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-secondary" />
            Emergency Contact
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-medium mb-1 block">Contact Name *</Label>
              <Input
                placeholder="Full name"
                value={formData.emergencyName}
                onChange={(e) => updateField('emergencyName', e.target.value)}
                className={errors.emergencyName ? 'border-red-500' : ''}
              />
              {errors.emergencyName && <p className="text-red-500 text-xs mt-1">{errors.emergencyName}</p>}
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-1 block">Phone Number *</Label>
              <Input
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={formData.emergencyPhone}
                onChange={(e) => updateField('emergencyPhone', e.target.value)}
                className={errors.emergencyPhone ? 'border-red-500' : ''}
              />
              {errors.emergencyPhone && <p className="text-red-500 text-xs mt-1">{errors.emergencyPhone}</p>}
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-1 block">Relationship *</Label>
              <Input
                placeholder="e.g., Spouse, Parent"
                value={formData.emergencyRelationship}
                onChange={(e) => updateField('emergencyRelationship', e.target.value)}
                className={errors.emergencyRelationship ? 'border-red-500' : ''}
              />
              {errors.emergencyRelationship && <p className="text-red-500 text-xs mt-1">{errors.emergencyRelationship}</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Health & Dietary */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Heart className="h-5 w-5 text-secondary" />
            Health & Dietary Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-1 block">Dietary Restrictions</Label>
            <Textarea
              placeholder="Vegetarian, vegan, gluten-free, halal, kosher, etc."
              value={formData.dietaryRestrictions}
              onChange={(e) => updateField('dietaryRestrictions', e.target.value)}
              rows={2}
            />
          </div>
          
          <div>
            <Label className="text-sm font-medium mb-1 block">Allergies</Label>
            <Textarea
              placeholder="Food allergies, medication allergies, etc."
              value={formData.allergies}
              onChange={(e) => updateField('allergies', e.target.value)}
              rows={2}
            />
          </div>
          
          <div>
            <Label className="text-sm font-medium mb-1 block">Medical Conditions</Label>
            <Textarea
              placeholder="Any medical conditions we should be aware of"
              value={formData.medicalConditions}
              onChange={(e) => updateField('medicalConditions', e.target.value)}
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Travel Insurance */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5 text-secondary" />
            Travel Insurance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-1 block">Do you have travel insurance? *</Label>
            <Select value={formData.hasInsurance} onValueChange={(value) => updateField('hasInsurance', value)}>
              <SelectTrigger className={errors.hasInsurance ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes, I have travel insurance</SelectItem>
                <SelectItem value="purchasing">I plan to purchase before the trip</SelectItem>
                <SelectItem value="no">No, I understand the risks</SelectItem>
              </SelectContent>
            </Select>
            {errors.hasInsurance && <p className="text-red-500 text-xs mt-1">{errors.hasInsurance}</p>}
          </div>
          
          {formData.hasInsurance === 'yes' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1 block">Insurance Provider</Label>
                <Input
                  placeholder="e.g., World Nomads, Allianz"
                  value={formData.insuranceProvider}
                  onChange={(e) => updateField('insuranceProvider', e.target.value)}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1 block">Policy Number</Label>
                <Input
                  placeholder="Your policy number"
                  value={formData.insurancePolicyNumber}
                  onChange={(e) => updateField('insurancePolicyNumber', e.target.value)}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Roommate Request (for group tours) */}
      {isGroupTour && (
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-secondary" />
              Roommate Preferences
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <Label className="text-sm font-medium mb-1 block">Roommate Request</Label>
              <Textarea
                placeholder="If traveling with a friend who booked separately, enter their name here. Otherwise, we'll pair you with another solo traveler of the same gender."
                value={formData.roommateRequest}
                onChange={(e) => updateField('roommateRequest', e.target.value)}
                rows={2}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submit Button */}
      <Button 
        type="submit" 
        className="w-full bg-secondary hover:bg-secondary/90"
        disabled={loading}
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
            Saving...
          </>
        ) : (
          'Save & Continue to Waiver'
        )}
      </Button>
    </form>
  );
};
